import type { KeyboardEvent } from "react";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { css, Global } from "@emotion/react";
import { Box } from "@mui/material";
import Cookies from "js-cookie";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import type { CuratedPlaceOfSafetyUnbranded } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  Membership,
  OrganisationMembership,
} from "@thalamos/aspire-common";
import {
  extendedMembershipToMembership,
  isAdminUser,
  isGuestUser,
  notificationChannelFns,
  NotificationEvents,
} from "@thalamos/aspire-common";

import { api, apiHooks, logout } from "./api.js";
import { TermsAndConditionsAccept } from "./components/layout/TermsAndConditions/TermsAndConditionsAccept.js";
import { config } from "./config.js";
import type { EmailRedirectEntry } from "./Contexts.js";
import {
  LoggedInUserContext,
  ManagedLocationsContext,
  NotificationsContext,
} from "./Contexts.js";
import { useChannel } from "./lib/pusher/useChannel.js";
import { useEvent } from "./lib/pusher/useEvent.js";
import { TrainingWelcomeDialog } from "./modals/TrainingWelcomeDialog/TrainingWelcomeDialog.js";
import { ClickThroughLaunchPage } from "./pages/ClickThroughLaunchPage.js";
import type { ContextSelectorPageProps } from "./pages/ContextSelector/ContextSelector.js";
import { ContextSelectorPage } from "./pages/ContextSelector/ContextSelector.js";
import { EMHFormsShareCodePage } from "./pages/eMhFormsShareCodePage.js";
import { GuestUserLoginPage } from "./pages/GuestUserLoginPage.js";
import ErrorBoundary from "./pages/helpers/ErrorBoundary/ErrorBoundary.js";
import { LoginPage } from "./pages/LoginPage.js";
import { NetworkMapWorkspace } from "./pages/NetworkMap/NetworkMapWorkspace.js";
import NotFound from "./pages/NotFound.js";
import { PathwayWorkspace } from "./pages/PathwayAnalytics/PathwayWorkspace.js";
import { rawRoutes, routeFns } from "./routePaths.js";
import { routes } from "./routes.js";
import * as tracing from "./tracing.js";
import {
  LoadingSpinner,
  ToastNotifications,
} from "~/components/design-system/index.js";
import { Layout } from "~/components/layout/index.js";
import { DialogsProvider } from "~/hooks/dialogs/index.js";

function AuthedApp({}: {}) {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user!;
  const navigate = useNavigate();

  // TODO: Remove this when we do the onboarding flow along with cookie-js
  useEffect(() => {
    if (!isGuestUser(user) && !isAdminUser(user)) {
      // We only want to redirect the user once to the profile page if the login count is 1
      if (user && user.loginCount === 1) {
        const redirectionFlag = Cookies.get(`${user.id}-profile-redirect`);

        if (!redirectionFlag) {
          navigate(`/profile/${user.id}`);
          Cookies.set(`${user.id}-profile-redirect`, "true", { expires: 1 }); // expires in 1 day
        }
      }
    }
  }, []);

  return (
    <>
      <TrainingWelcomeDialog />
      <TermsAndConditionsAccept />

      <Box sx={{ mt: 3 }}>
        <Routes>
          {routes.map((r) => (
            <Route
              key={r.breadcrumb}
              path={r.path}
              element={<r.component />}
            ></Route>
          ))}
          <Route path={"*"} element={<NotFound />}></Route>
        </Routes>
      </Box>
    </>
  );
}

function WithNotifications({ children }: { children: ReactElement }) {
  const userContext = useContext(LoggedInUserContext);

  const contextId =
    userContext?.user?.sessionContext?.type === "team"
      ? userContext?.user?.sessionContext?.teamId
      : userContext?.user?.sessionContext?.organisationId;

  const [notificationsPage, setNotificationsPage] = useState(0);

  const notificationsPageSize = 100;

  const [{ data: notifications }, refetchNotifications] =
    apiHooks.notifications.get(contextId, {
      limit: notificationsPageSize,
      offset: notificationsPage * notificationsPageSize,
    });

  useEffect(() => {
    refetchNotifications();
  }, [contextId, notificationsPage]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications: notifications!,
        setNotificationsPage,
        notificationsPage,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

function WithDialogs({ children }: { children: ReactElement }) {
  return <DialogsProvider>{children}</DialogsProvider>;
}

function WithAdminManagedLocations({ children }: { children: ReactElement }) {
  const [{ data: response }, refetchLocations] =
    apiHooks.configuration.getAdminManagedLocations();

  const [{ data: dataSharingPartnerships }] =
    apiHooks.dataSharingPartnership.getAll();

  const [curatedPlacesOfSafety, setCuratedPlacesOfSafety] = useState<
    CuratedPlaceOfSafetyUnbranded[]
  >([]);
  const [placesOfSafetyLoaded, setPlacesOfSafetyLoaded] = useState(false);
  const [emailRedirects, setEmailRedirects] = useState<EmailRedirectEntry[]>(
    [],
  );
  const [emailRedirectsLoaded, setEmailRedirectsLoaded] = useState(false);

  useEffect(() => {
    api.placeOfSafety
      .getAll()
      .then((res) => {
        if (res.status === 200) {
          setCuratedPlacesOfSafety(res.data.results);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch curated places of safety:", err);
      })
      .finally(() => {
        setPlacesOfSafetyLoaded(true);
      });

    api.configuration
      .getActiveEmailRedirects()
      .then((res) => {
        if (res.status === 200) {
          setEmailRedirects(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch email redirects:", err);
      })
      .finally(() => {
        setEmailRedirectsLoaded(true);
      });
  }, []);

  if (
    !response ||
    !placesOfSafetyLoaded ||
    !dataSharingPartnerships ||
    !emailRedirectsLoaded
  ) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "grid",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <LoadingSpinner label="Loading..." />
      </Box>
    );
  }

  return (
    <ManagedLocationsContext.Provider
      value={{
        managedLocations: response?.locations || [],
        trusts: response?.trusts || [],
        curatedPlacesOfSafety,
        dataSharingPartnerships: dataSharingPartnerships ?? [],
        emailRedirects,
        refetchManagedLocations: refetchLocations || (() => {}),
      }}
    >
      {children}
    </ManagedLocationsContext.Provider>
  );
}

function LoggedOutHomePage({}: {}) {
  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        justifyItems: "center",
        alignItems: "center",
      }}
    >
      <LoadingSpinner label="Loading..." />
    </Box>
  );
}

// Toggles the display of testid labels
function TestIdDebugger() {
  let [debugLabelsVisible, setDebugLabelsVisible] = useState(false);

  useEffect(() => {
    const handleKeyPress = function (event: KeyboardEvent) {
      if (
        event.code == "KeyT" &&
        event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        setDebugLabelsVisible(!debugLabelsVisible);
      }
    } as any;

    // attach the event listener
    document.addEventListener("keydown", handleKeyPress);

    // remove the event listener
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [debugLabelsVisible]);

  if (debugLabelsVisible) {
    return (
      <Global
        styles={css`
          [data-testid] {
            position: relative;
          }

          [data-testid]::before {
            display: block;
            content: attr(data-testid);
            position: absolute;
            left: 0;
            top: 0;
            clear: both;
            width: max-content;
            padding: 3px 6px;
            background-color: hotpink;
            color: white;
            font-size: 10px;
            line-height: 1;
            text-transform: none;
            letter-spacing: normal;
          }

          [data-testid] > [data-testid]::before {
            left: auto;
            right: 0;
          }

          [data-testid] > :not([data-testid]) > [data-testid]::before {
            left: auto;
            right: 0;
          }
        `}
      />
    );
  }

  return null;
}

let loginCheckInterval: ReturnType<typeof setInterval> | undefined = undefined;

function WithLoggedInUser({ children }: { children: ReactElement }) {
  const navigate = useNavigate();
  const location = useLocation();

  // The URL path without the query string. Redirecting to this URL effectively removes
  // all query string parameters. Normalise consecutive slashes to avoid redirect loops
  // when the user navigates to a URL like https://example.com//.
  const pathname = window.location.pathname.replace(/\/+/g, "/");
  const qs = new URLSearchParams(location.search);

  const guestUserIdFromQueryParams = qs.get("guest-user-id");
  const contextIdFromParams = qs.get("context-id");

  // Track the initial value of contextIdFromParams
  const initialContextIdRef = useRef<string | null | undefined>(
    contextIdFromParams,
  );

  // Fetch user data
  const [{ data: user, response: userMeResponse }, refetchUser] =
    apiHooks.users.me(contextIdFromParams || guestUserIdFromQueryParams);

  // Refetch user data if pusher channel is triggers
  const userChannel = useChannel(notificationChannelFns.user.profile(user?.id));
  useEvent(userChannel, NotificationEvents.resourceReload, () => {
    refetchUser();
  });

  // Determine whether user is logged in
  const amILoggedIn = userMeResponse?.status === 200 && user;

  // We ping /api/health/authed-ping every 5 seconds to check that the session is still active.
  // Any API request _other_ than /api/health/authed-ping will refresh the session, so this is
  // essentially our way of doing session inactivity timeouts
  const CHECK_LOGIN_STATUS_INTERVAL_MS = 1000 * 10;
  useEffect(() => {
    if (loginCheckInterval) {
      clearInterval(loginCheckInterval as any);
    }
    if (amILoggedIn) {
      loginCheckInterval = setInterval(
        api.users.loggedInCheck,
        CHECK_LOGIN_STATUS_INTERVAL_MS,
      );
    }
  }, [user, amILoggedIn]);

  // Set user for Datadog tracing
  useEffect(() => {
    tracing.setUser(user || null);
  }, [user]);

  const hasReturnToParam = qs.has("returnTo");
  const isContextSelectorPage = location.pathname.startsWith(
    routeFns.contextSelector(),
  );

  // Set the user's team context (or else redirect to the context selector)
  useEffect(() => {
    if (amILoggedIn) {
      const membershipFromQueryParam = user.memberships.find((m: Membership) =>
        m.role === "member" && m.type === "team"
          ? m.teamId === contextIdFromParams
          : (m as OrganisationMembership).organisationId ===
            contextIdFromParams,
      );

      // Remove the context-id param so we never get stuck in a loop
      qs.delete("context-id");

      if (user.sessionContext) {
        const previousUserId = localStorage.getItem("userId");
        const previousTeamId = localStorage.getItem("teamId");
        const previousRoleId = localStorage.getItem("roleId");

        const userId = user.id;
        const roleId = user.sessionContext.role;
        const teamId = user.sessionContext.teamId;

        localStorage.setItem("userId", userId || "");
        localStorage.setItem("teamId", teamId || "");
        localStorage.setItem("roleId", roleId || "");

        // if it is set and doesn't match navigate to /
        const contextHasChanged =
          (previousUserId && previousUserId !== userId) ||
          (previousTeamId && previousTeamId !== teamId) ||
          (previousRoleId && previousRoleId !== roleId);

        const shouldRedirectToHome =
          !initialContextIdRef.current &&
          contextHasChanged &&
          !pathname.startsWith(rawRoutes.visionPreviewIncident.path);

        if (shouldRedirectToHome) {
          navigate("/", { replace: true });
        }
      }

      const newPathname = qs.size ? `${pathname}?${qs.toString()}` : pathname;

      if (membershipFromQueryParam) {
        api.users
          .setSessionContext(
            extendedMembershipToMembership(membershipFromQueryParam),
          )
          .then(() => {
            refetchUser();
            navigate(newPathname, { replace: true });
          });
      } else if (!user.sessionContext) {
        const onlyMembership =
          user.memberships.length === 1 ? user.memberships[0] : undefined;
        if (onlyMembership !== undefined) {
          api.users
            .setSessionContext(extendedMembershipToMembership(onlyMembership))
            .then(() => {
              refetchUser();
              navigate(newPathname, { replace: true });
            });
          // We should only add the returnTo param if it hasn't already been set
        } else if (!isContextSelectorPage && !hasReturnToParam) {
          navigate(routeFns.contextSelector(newPathname));
        }
      }
    }
  }, [user]);

  // Render team context selector
  if (amILoggedIn && isContextSelectorPage) {
    function transformMemberships(
      memberships: ExtendedThalamosUser["memberships"],
    ) {
      return memberships.map((membership) => ({
        type: membership.type,
        ...(membership.type === "team"
          ? { teamId: membership.teamId }
          : { organisationId: membership.organisationId }),
        role: membership.role,
        name:
          membership.type === "team"
            ? membership.teamName
            : membership.organisationName,
      }));
    }

    return (
      <ContextSelectorPage
        userId={user.id}
        userName={user.name}
        defaultSelectedContext={user.defaultSelectedContext}
        memberships={
          transformMemberships(
            user.memberships,
          ) as ContextSelectorPageProps["memberships"]
        }
        sessionContext={!!user.sessionContext}
        renderLogoutFn={logout}
        reloadUser={refetchUser}
      />
    );
  }

  // Render loading spinner
  if (!amILoggedIn || !user?.sessionContext) {
    return (
      <Layout>
        <LoggedOutHomePage />
      </Layout>
    );
  }

  // Render main app
  return (
    <LoggedInUserContext.Provider
      value={{
        user,
        refetchUser,
      }}
    >
      {children}
    </LoggedInUserContext.Provider>
  );
}
export function App() {
  const location = useLocation();

  if (location.pathname.startsWith(routeFns.guestUserLogin()))
    return <GuestUserLoginPage />;
  else if (location.pathname.startsWith(routeFns.emhFormsShareCodePage()))
    return <EMHFormsShareCodePage />;
  else if (location.pathname.startsWith(routeFns.loginPage("")))
    return <LoginPage />;
  // Pre-login interstitial for clickthrough remembered-login. Must be public
  // (it runs before authentication and redirects to /api/oauth/login itself).
  else if (location.pathname.startsWith(rawRoutes.clickThroughLaunch.path))
    return <ClickThroughLaunchPage />;
  // Pathway analytics: a distinct, chromeless workspace opened in its own window
  // from the admin menu. Rendered inside WithLoggedInUser (so it has the session
  // + user, reusing the aspire cookie) but OUTSIDE <Layout> — no aspire nav.
  else if (location.pathname.startsWith(routeFns.pathwayAnalytics()))
    return (
      <WithLoggedInUser>
        <PathwayWorkspace />
      </WithLoggedInUser>
    );
  // Connectivity network map: same chromeless-in-its-own-window treatment as
  // pathway analytics (inside WithLoggedInUser, outside <Layout>).
  else if (location.pathname.startsWith(routeFns.networkMap()))
    return (
      <WithLoggedInUser>
        <NetworkMapWorkspace />
      </WithLoggedInUser>
    );
  else
    return (
      <>
        <WithLoggedInUser>
          <WithNotifications>
            <WithDialogs>
              <WithAdminManagedLocations>
                <Layout>
                  {config.enableTestIdVizualizer && <TestIdDebugger />}
                  <ToastNotifications />
                  <ErrorBoundary location={location}>
                    <AuthedApp />
                  </ErrorBoundary>
                </Layout>
              </WithAdminManagedLocations>
            </WithDialogs>
          </WithNotifications>
        </WithLoggedInUser>
      </>
    );
}
